import { useMemo } from 'react';
import type { RecruitIdeaPath } from './RecruitIdeaPath';
import { readClientNotes, setClientNotesRootField, updateClientNotes, useClientNotes } from '../clientNotesStore';
import type { ClientNotes } from '../clientNotesStore';
import type { RecruitmentNotes } from '../../../../store/clientNotes/api';

type RecruitWho = NonNullable<RecruitmentNotes['who']>;

/**
 * 他是誰 — one copy per client (§3, §13 R1): 現職 and 介紹人 are the
 * factsheet's own `occupation` / `referrerName`; 年資 / 人脈 / 目標月入 are
 * `factsheet.recruitment.who`.
 */
export type RecruitIdeaWho = {
  occupation: string;
  yearsOfExp: number | null;
  network: string;
  targetIncome: number | null;
  referrer: string;
};

const numberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const whoOf = (notes: ClientNotes | null): RecruitIdeaWho | null => {
  if (!notes) return null;
  const who = notes.recruitment.who || {};
  const value: RecruitIdeaWho = {
    occupation: notes.occupation || '',
    yearsOfExp: numberOrNull(who.yearsOfExp),
    network: who.network || '',
    targetIncome: numberOrNull(who.targetIncome),
    referrer: notes.referrerName || ''
  };
  const empty =
    !value.occupation && value.yearsOfExp === null && !value.network && value.targetIncome === null && !value.referrer;
  return empty ? null : value;
};

export const readRecruitIdeaWho = (clientId?: string | null): RecruitIdeaWho | null => whoOf(readClientNotes(clientId));

/** `null` resets the adviser's 他是誰 notes; the root fields are real factsheet data and stay. */
export const saveRecruitIdeaWho = (clientId: string | null | undefined, value: RecruitIdeaWho | null): void => {
  if (!clientId) return;
  if (value === null) {
    void updateClientNotes(clientId, { recruitment: { who: null } }, notes => {
      const recruitment = { ...notes.recruitment };
      delete recruitment.who;
      return { ...notes, recruitment };
    });
    return;
  }
  setClientNotesRootField(clientId, 'occupation', value.occupation.trim().slice(0, 120));
  setClientNotesRootField(clientId, 'referrerName', value.referrer.trim().slice(0, 120));
  const network = value.network.trim().slice(0, 120);
  saveRecruitWhoNotes(clientId, {
    yearsOfExp: value.yearsOfExp,
    network: network || null,
    targetIncome: value.targetIncome
  });
};

/** Only the adviser-notes half of 他是誰 (年資 / 人脈 / 目標月入) — 現職 and 介紹人 untouched. */
export const saveRecruitWhoNotes = (
  clientId: string | null | undefined,
  body: { yearsOfExp: number | null; network: string | null; targetIncome: number | null }
): void => {
  if (!clientId) return;
  const who: RecruitWho = {};
  if (body.yearsOfExp !== null) who.yearsOfExp = body.yearsOfExp;
  if (body.network) who.network = body.network;
  if (body.targetIncome !== null) who.targetIncome = body.targetIncome;
  // Nothing changed, nothing to send (saving 他是誰 for 現職 alone is common).
  const current: RecruitWho = readClientNotes(clientId)?.recruitment.who || {};
  if (
    (current.yearsOfExp ?? null) === (who.yearsOfExp ?? null) &&
    (current.network || null) === (who.network || null) &&
    (current.targetIncome ?? null) === (who.targetIncome ?? null)
  ) {
    return;
  }
  void updateClientNotes(clientId, { recruitment: { who: body } }, notes => ({
    ...notes,
    recruitment: { ...notes.recruitment, who }
  }));
};

export const useRecruitIdeaWho = (clientId?: string | null, _path?: RecruitIdeaPath | null) => {
  const notes = useClientNotes(clientId);
  const value = useMemo(() => whoOf(notes), [notes]);
  return { value, save: (next: RecruitIdeaWho | null) => saveRecruitIdeaWho(clientId, next) };
};
